/**
 * masterGanttHelpers.ts
 * BUG-2026-0079 — small pure helpers for the Master Gantt view.
 *
 * `flattenMasterGanttBookings()` walks the hierarchical MasterGanttResponse
 * (projects → workOrders → machineBookings) and emits a flat array shaped
 * like a subset of MachineBookingCalendar. This lets us reuse the
 * MachineScheduleFilters component (which derives its Quote / Work Order
 * dropdown options by iterating a flat `bookingsInWindow` list) on the
 * Master Gantt route without duplicating the filter UI.
 *
 * The narrowed type is deliberate: MachineScheduleFilters only reads
 * `TaskQuoteKey`, `TaskWorkOrderKey`, and `TaskName` from each booking
 * (see lines 174–216 of MachineScheduleFilters.tsx), so producing the full
 * MachineBookingCalendar shape from the Master Gantt response — which
 * doesn't carry all those joined fields — would be a lie. The narrow type
 * keeps the contract honest.
 *
 * BUG-2026-0079 also extends Master Gantt filtering with quoteKey /
 * workOrderKey. `applyMasterGanttQuoteWorkOrderFilter()` returns a copy of
 * the response with non-matching machineBookings + employeeAssignments
 * pruned.
 *
 * BUG-2026-0080: when a filter is active, work orders that survive the
 * filter with no bookings AND no assignments are dropped, and projects
 * whose work-order list becomes empty are dropped as well. Previously the
 * helper kept those empty rows under a "preserves Highcharts row identity"
 * rationale, but in practice 30+ empty WO rows collapsed the y-axis labels
 * into overlapping "black lines" and ultimately crashed the Chromium
 * renderer process. The chart now renders only the matching rows.
 */

import type {
  LabSection,
  MachineBookingSource,
  MasterGanttResponse,
} from '../../../types/schedule';
import type { FilterBookingRow, MachineSummaryForFilter } from './MachineScheduleFilters';
import {
  applyMasterGanttFilters,
  type MasterGanttFilterState,
} from './useMasterGanttFilters';

/**
 * Distinct machines referenced by the response's bookings, for the toolbar's
 * Machine filter (sorted by code). Moved out of MasterGanttPage (BUG-2026-0194,
 * file-size cap).
 *
 * BUG-2026-0207: carries the machine's LabSection (the API has returned
 * `machineLabSection` on every booking since Phase 4) so the Machine dropdown
 * narrows to the selected sections instead of emptying out.
 */
export function collectMachinesForFilter(
  response: MasterGanttResponse | null,
): MachineSummaryForFilter[] {
  if (!response) return [];
  const seen = new Map<string, MachineSummaryForFilter>();
  for (const project of response.projects) {
    for (const wo of project.workOrders) {
      for (const b of wo.machineBookings) {
        if (!seen.has(b.machineCode)) {
          seen.set(b.machineCode, {
            machineKey: b.machineKey,
            code: b.machineCode,
            name: b.machineName,
            labSection: (b.machineLabSection as LabSection | null) ?? null,
          });
        }
      }
    }
  }
  return Array.from(seen.values()).sort((a, b) => a.code.localeCompare(b.code));
}

/**
 * BUG-2026-0207 — apply the booking-level filters (Toggle Proposals, Lab
 * Section, Machine, Work Type) to a Master Gantt response.
 *
 * The Lab Section filter was previously never applied on this route: the
 * page's inline filter only looked at status / machineCode / source and left
 * `labSections` untouched, so picking "Rock" changed nothing. Machine bookings
 * now go through the SAME predicate the URL-sticky filter hook exposes
 * (`applyMasterGanttFilters`, null section → 'Other').
 *
 * Staff (employee assignment) bars carry no machine. They honour the
 * proposals / work-type filters directly, and under a MACHINE-SCOPED filter
 * (Lab Section and/or Machine) they follow their work order: a WO that keeps
 * at least one matching machine booking keeps its people ("programs that have
 * rock testing" — the submitter's wording), a WO that keeps none loses them.
 *
 * Empty WOs and empty projects are pruned (BUG-2026-0080 — leftover empty
 * rows collapsed the y-axis and crashed the renderer). Returns the input
 * unchanged (same reference) when no booking-level filter is active.
 */
export function applyMasterGanttBookingFilters(
  ganttData: MasterGanttResponse,
  state: MasterGanttFilterState,
): MasterGanttResponse {
  const machineScoped = state.labSections.length > 0 || state.machineCodes.length > 0;
  const noBookingFilters =
    state.showProposals && !machineScoped && state.workTypes.length === 0;
  if (noBookingFilters) return ganttData;

  const keepAssignment = (a: { status: string; source: string }): boolean => {
    if (!state.showProposals && a.status === 'Tentative') return false;
    if (state.workTypes.length > 0 &&
        !state.workTypes.includes(a.source as MachineBookingSource)) return false;
    return true;
  };

  return {
    projects: ganttData.projects
      .map((project) => ({
        ...project,
        workOrders: project.workOrders
          .map((wo) => {
            const machineBookings = applyMasterGanttFilters(wo.machineBookings, state);
            const employeeAssignments =
              machineScoped && machineBookings.length === 0
                ? []
                : wo.employeeAssignments.filter(keepAssignment);
            if (machineBookings.length === 0 && employeeAssignments.length === 0) {
              return null;
            }
            return { ...wo, machineBookings, employeeAssignments };
          })
          .filter((wo): wo is NonNullable<typeof wo> => wo !== null),
      }))
      .filter((project) => project.workOrders.length > 0),
  };
}

/**
 * BUG-2026-0207 — every URL-sticky Master Gantt filter in one pure call:
 * booking-level filters first, then the quote / work-order filter. Identity-
 * stable when nothing is active, so callers can memoise on the result.
 */
export function applyMasterGanttFilterState(
  ganttData: MasterGanttResponse,
  state: MasterGanttFilterState,
): MasterGanttResponse {
  return applyMasterGanttQuoteWorkOrderFilter(
    applyMasterGanttBookingFilters(ganttData, state),
    state.quoteKey,
    state.workOrderKey,
  );
}

/**
 * Re-export the narrow row shape from MachineScheduleFilters under a more
 * descriptive name for callers on the Master Gantt route. Same underlying
 * Pick<MachineBookingCalendar, ...> — keeps a single source of truth.
 */
export type FilterDropdownBookingRow = FilterBookingRow;

/**
 * Flatten a MasterGanttResponse into one FilterDropdownBookingRow per
 * machine booking. The returned array is always non-null (empty when the
 * response is null or has zero bookings, so the caller can spread it into
 * a prop without an extra guard).
 *
 * Source-of-truth: backend/src/services/masterGanttService.ts populates
 *   projects[].workOrders[].machineBookings[]
 * across three buckets:
 *   - Bucket A (WorkOrder-sourced): wo.workOrderKey is a real WorkOrderKey.
 *   - Bucket B (Quote-sourced):     wo.workOrderKey is actually the QuoteKey
 *                                   (synthesised "Pending Quotes" project).
 *   - Bucket C (Manual events):     synthetic WO; booking.source = 'Manual'.
 *
 * We discriminate on `booking.source` so the WO and Quote dropdowns surface
 * the right entries:
 *   - source === 'Quote'     → TaskQuoteKey = wo.workOrderKey (which is the
 *                              QuoteKey for Bucket B), TaskWorkOrderKey = null.
 *   - source === 'WorkOrder' → TaskWorkOrderKey = wo.workOrderKey,
 *                              TaskQuoteKey = null.
 *   - source === 'Manual'    → both null; the booking does not surface on
 *                              either dropdown (filter scope is task-based).
 *
 * The label preference order matches the Machine-Schedule convention:
 * task name → work-order name → work-order number. The fallback chain
 * ensures the dropdown row always renders something readable.
 */
export function flattenMasterGanttBookings(
  ganttData: MasterGanttResponse | null,
): FilterDropdownBookingRow[] {
  if (!ganttData) return [];

  const out: FilterDropdownBookingRow[] = [];
  for (const project of ganttData.projects) {
    for (const wo of project.workOrders) {
      for (const booking of wo.machineBookings) {
        const isQuoteSourced = booking.source === 'Quote';
        const isWorkOrderSourced = booking.source === 'WorkOrder';
        out.push({
          TaskQuoteKey:     isQuoteSourced     ? wo.workOrderKey : null,
          TaskWorkOrderKey: isWorkOrderSourced ? wo.workOrderKey : null,
          TaskName: booking.taskName ?? wo.workOrderName ?? wo.workOrderNumber,
        });
      }
    }
  }
  return out;
}

/**
 * Apply the Master-Gantt quoteKey / workOrderKey filters to a response,
 * returning a structurally-cloned response with non-matching bookings,
 * assignments, empty WOs, and empty projects pruned.
 *
 * Discrimination matches `flattenMasterGanttBookings`:
 *   - quoteKey filter      → keep bookings where source === 'Quote' AND
 *                            wo.workOrderKey === quoteKey (because Bucket B
 *                            stores the QuoteKey in wo.workOrderKey).
 *   - workOrderKey filter  → keep bookings where source === 'WorkOrder' AND
 *                            wo.workOrderKey === workOrderKey.
 *
 * If both keys are null this returns the input unchanged (referentially —
 * the caller can use this as a fast-path identity check).
 *
 * BUG-2026-0080: a previous revision intentionally preserved non-matching
 * WOs as `{...wo, machineBookings: [], employeeAssignments: []}` to keep
 * the Highcharts row order stable across filter changes. In practice this
 * caused 30+ empty WO rows to be rendered alongside the 1 matching row,
 * collapsing y-axis labels and exhausting the renderer process. Empty
 * surviving WOs and empty projects are now dropped.
 */
export function applyMasterGanttQuoteWorkOrderFilter(
  ganttData: MasterGanttResponse,
  quoteKey: string | null,
  workOrderKey: string | null,
): MasterGanttResponse {
  if (quoteKey === null && workOrderKey === null) return ganttData;

  const keepBookingForSource = (
    source: string,
    woKeyMatchesQuote: boolean,
    woKeyMatchesWO: boolean,
  ): boolean => {
    if (quoteKey !== null && source === 'Quote' && woKeyMatchesQuote) return true;
    if (workOrderKey !== null && source === 'WorkOrder' && woKeyMatchesWO) return true;
    return false;
  };

  return {
    projects: ganttData.projects
      .map((project) => ({
        ...project,
        workOrders: project.workOrders
          .map((wo) => {
            const woKeyMatchesQuote = quoteKey !== null && wo.workOrderKey === quoteKey;
            const woKeyMatchesWO    = workOrderKey !== null && wo.workOrderKey === workOrderKey;

            // Only emit a WO if at least one of the active filters could
            // match this WO's source. (Quote-only filter against a
            // WorkOrder-sourced WO contributes nothing, and vice-versa.)
            if (!woKeyMatchesQuote && !woKeyMatchesWO) return null;

            const machineBookings = wo.machineBookings.filter((b) =>
              keepBookingForSource(b.source, woKeyMatchesQuote, woKeyMatchesWO),
            );
            const employeeAssignments = wo.employeeAssignments.filter((a) =>
              keepBookingForSource(a.source, woKeyMatchesQuote, woKeyMatchesWO),
            );

            if (machineBookings.length === 0 && employeeAssignments.length === 0) {
              return null;
            }

            return { ...wo, machineBookings, employeeAssignments };
          })
          .filter((wo): wo is NonNullable<typeof wo> => wo !== null),
      }))
      .filter((project) => project.workOrders.length > 0),
  };
}
